import React, { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
} from "@mui/material";
import type { Portal } from "@/types";
import { FormProvider, ColorPicker, LogoUpload } from "@/components/form";

export interface BrandingFormValues {
  logo_url: string;
  primary_color: string;
  secondary_color: string;
}

interface BrandingTabProps {
  portal: Portal;
  onSave: (data: BrandingFormValues) => void;
  isUpdating: boolean;
}

const BrandingTab: React.FC<BrandingTabProps> = ({ portal, onSave, isUpdating }) => {
  const methods = useForm<BrandingFormValues>({
    defaultValues: {
      logo_url: portal.branding?.logo_url ?? "",
      primary_color: portal.branding?.primary_color ?? "",
      secondary_color: portal.branding?.secondary_color ?? "",
    },
  });

  const logoUrl = useWatch({ control: methods.control, name: "logo_url" });

  useEffect(() => {
    methods.reset({
      logo_url: portal.branding?.logo_url ?? "",
      primary_color: portal.branding?.primary_color ?? "",
      secondary_color: portal.branding?.secondary_color ?? "",
    });
  }, [portal, methods]);

  return (
    <FormProvider methods={methods} onSubmit={onSave}>
      <Stack spacing={2}>
        <LogoUpload
          label="Logo"
          value={logoUrl}
          onChange={(v) => methods.setValue("logo_url", v, { shouldDirty: true })}
          disabled={isUpdating}
        />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ColorPicker name="primary_color" label="Primary color" disabled={isUpdating} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ColorPicker name="secondary_color" label="Secondary color" disabled={isUpdating} />
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="flex-end">
          <Button type="submit" variant="contained" disabled={isUpdating}>
            {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Save Branding"}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

export default BrandingTab;
